import { Camera, Glasses, Mic, Palette } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { JSX } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';

import { cn } from '@/lib/utils';

import type {
  BackgroundMode,
  BackgroundQualitySetting,
  ResolvedBackgroundQuality,
} from '../media/backgroundProcessor';
import type { BlurStrength, VideoFilter } from '../media/videoEffects';
import type { FaceMaskId } from '../media/faceMasks';
import {
  BackgroundModePicker,
  BackgroundQualityPicker,
  BlurStrengthPicker,
  FaceMaskPicker,
  VideoFilterPicker,
} from './BackgroundPickers';
import type { AudioEffectKey, AudioEffectSettings } from './micEffects';

/**
 * The «Эффекты» dialog: the single effects surface reachable from the prejoin options and the
 * in-call «Ещё» menu. The microphone section toggles the three browser processing switches
 * (noise suppression, echo cancellation, auto gain); each row shows the LIVE effective state
 * read from the active track's settings when available. The «Фильтры» section picks a
 * whole-frame color filter (applied in the composite - the remote side sees it). The camera
 * section re-homes the background mode/quality pickers plus the blur strength row (all shared
 * with the prejoin preview chips). Every camera choice applies LIVE: on the prejoin it drives
 * the preview pipeline, in-call the preference-store subscription swaps the published camera
 * track through the replaceTrack machine (room/cameraEffects.ts) without a rejoin.
 */

/** Camera section bindings: prejoin passes its live state, the room passes the stored prefs. */
export interface EffectsDialogCameraProps {
  readonly bgMode: BackgroundMode;
  readonly bgQuality: BackgroundQualitySetting;
  /** What the "auto" benchmark resolved to (shown next to the auto chip); null when unknown. */
  readonly resolvedQuality: ResolvedBackgroundQuality | null;
  /** The chosen blur strength (shown while the blur treatment is active). */
  readonly blurStrength: BlurStrength;
  readonly onBgMode: (mode: BackgroundMode) => void;
  readonly onBgQuality: (quality: BackgroundQualitySetting) => void;
  readonly onBlurStrength: (strength: BlurStrength) => void;
}

/** Filters section bindings: the whole-frame color filter choice. */
export interface EffectsDialogFiltersProps {
  readonly filter: VideoFilter;
  readonly onFilter: (filter: VideoFilter) => void;
}

/** Masks section bindings: the face-mask overlay choice. */
export interface EffectsDialogMasksProps {
  readonly mask: FaceMaskId;
  readonly onMask: (mask: FaceMaskId) => void;
}

/** Microphone section bindings: the stored switches plus the live effective state. */
export interface EffectsDialogMicrophoneProps {
  readonly settings: AudioEffectSettings;
  /** Effective state from the active track's getSettings(); null when no live track reports it. */
  readonly effective: AudioEffectSettings | null;
  /** True while an in-call switch is being applied (rows disable to prevent double submits). */
  readonly busy: boolean;
  readonly onToggle: (key: AudioEffectKey, value: boolean) => void;
}

export interface EffectsDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly camera: EffectsDialogCameraProps;
  readonly filters: EffectsDialogFiltersProps;
  readonly masks: EffectsDialogMasksProps;
  readonly microphone: EffectsDialogMicrophoneProps;
}

/** The three toggle rows in their dialog order (testids pinned by unit and e2e tests). */
const EFFECT_ROWS: readonly { key: AudioEffectKey; testid: string; labelKey: string }[] = [
  { key: 'noiseSuppression', testid: 'effects-ns', labelKey: 'effects.mic_ns' },
  { key: 'echoCancellation', testid: 'effects-ec', labelKey: 'effects.mic_ec' },
  { key: 'autoGainControl', testid: 'effects-agc', labelKey: 'effects.mic_agc' },
];

export function EffectsDialog(props: EffectsDialogProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent
        data-testid="effects-dialog"
        className="border-white/10 bg-zinc-900 text-zinc-100 sm:max-w-[420px]"
      >
        <DialogHeader>
          <DialogTitle className="text-[16px] font-semibold tracking-tight text-white">
            {t('effects.title')}
          </DialogTitle>
          <DialogDescription className="text-[12.5px] text-zinc-400">
            {t('effects.description')}
          </DialogDescription>
        </DialogHeader>

        {/* Microphone: the three processing switches with their live effective state. */}
        <section aria-label={t('effects.mic_section')} className="flex flex-col gap-1">
          <SectionHeading icon={Mic} label={t('effects.mic_section')} />
          <div className="divide-y divide-white/[0.06]">
            {EFFECT_ROWS.map((row) => (
              <EffectToggleRow
                key={row.key}
                testid={row.testid}
                label={t(row.labelKey)}
                checked={props.microphone.settings[row.key]}
                effective={
                  props.microphone.effective === null ? null : props.microphone.effective[row.key]
                }
                busy={props.microphone.busy}
                onToggle={(value) => {
                  props.microphone.onToggle(row.key, value);
                }}
              />
            ))}
          </div>
          <p className="mt-1 text-[11px] leading-snug text-zinc-500">
            {t('effects.mic_restart_hint')}
          </p>
        </section>

        {/* Filters: whole-frame color presets, applied in the composite (visible remotely). */}
        <section aria-label={t('effects.filters_section')} className="flex flex-col gap-2">
          <SectionHeading icon={Palette} label={t('effects.filters_section')} />
          <VideoFilterPicker
            filter={props.filters.filter}
            onFilter={props.filters.onFilter}
            testidPrefix="filter"
            wrapperTestid="filter-row"
          />
        </section>

        {/* Masks: face-anchored overlays, computed locally on the device (privacy hint below). */}
        <section aria-label={t('effects.masks_section')} className="flex flex-col gap-2">
          <SectionHeading icon={Glasses} label={t('effects.masks_section')} />
          <FaceMaskPicker
            mask={props.masks.mask}
            onMask={props.masks.onMask}
            testidPrefix="mask"
            wrapperTestid="mask-row"
          />
          <p className="text-[11px] leading-snug text-zinc-500">{t('effects.masks_privacy')}</p>
        </section>

        {/* Camera: the background treatment + quality + blur strength pickers, re-homed here. */}
        <section aria-label={t('effects.cam_section')} className="flex flex-col gap-2">
          <SectionHeading icon={Camera} label={t('effects.cam_section')} />
          <div className="flex flex-col items-start gap-1.5">
            <BackgroundModePicker
              mode={props.camera.bgMode}
              onMode={props.camera.onBgMode}
              testidPrefix="effects-bg-mode"
            />
            {props.camera.bgMode !== 'none' ? (
              <BackgroundQualityPicker
                quality={props.camera.bgQuality}
                onQuality={props.camera.onBgQuality}
                resolvedQuality={props.camera.resolvedQuality}
                testidPrefix="effects-bg-quality"
                wrapperTestid="effects-bg-quality"
              />
            ) : null}
            {props.camera.bgMode === 'blur' ? (
              <BlurStrengthPicker
                strength={props.camera.blurStrength}
                onStrength={props.camera.onBlurStrength}
                testidPrefix="effects-blur-strength"
                wrapperTestid="effects-blur-strength"
              />
            ) : null}
          </div>
          <p className="text-[11px] leading-snug text-zinc-500">{t('effects.cam_live_note')}</p>
        </section>
      </DialogContent>
    </Dialog>
  );
}

/** One dialog section heading: a small uppercase row with the kind icon. */
function SectionHeading(props: { readonly icon: typeof Mic; readonly label: string }): JSX.Element {
  const Icon = props.icon;
  return (
    <p className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-zinc-500">
      <Icon className="size-3" aria-hidden />
      {props.label}
    </p>
  );
}

/**
 * One microphone switch row: label, the live effective badge («вкл»/«выкл» read from the
 * track's settings, dimmed to the stored preference when no live track reports) and the switch.
 */
function EffectToggleRow(props: {
  readonly testid: string;
  readonly label: string;
  readonly checked: boolean;
  readonly effective: boolean | null;
  readonly busy: boolean;
  readonly onToggle: (value: boolean) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      data-testid={props.testid}
      data-effective={props.effective === null ? 'unknown' : props.effective ? 'on' : 'off'}
      className="flex items-center justify-between gap-3 py-2.5"
    >
      <span className="flex min-w-0 flex-col">
        <span className="text-[12.5px] text-zinc-200">{props.label}</span>
        <span
          className={cn(
            'text-[10.5px] tabular-nums',
            props.effective === null ? 'text-zinc-600' : 'text-emerald-400',
          )}
        >
          {props.effective === null
            ? t('effects.effective_from_pref', {
                value: props.checked ? t('effects.effective_on') : t('effects.effective_off'),
              })
            : props.effective
              ? t('effects.effective_on')
              : t('effects.effective_off')}
        </span>
      </span>
      <Switch
        data-testid={`${props.testid}-switch`}
        checked={props.checked}
        disabled={props.busy}
        onCheckedChange={props.onToggle}
        className="scale-90 data-[state=checked]:bg-emerald-500"
      />
    </div>
  );
}
